import { useCallback, useEffect } from 'react'
import { useMutation } from '@truepill/tpos-react-router'
import { PrinterPurpose } from '@truepill/tpos-types'
import { GENERATE_SHIPPING_LABEL, PRINT_SHIPPING_LABEL } from 'gql'
import useErrorToast from 'hooks/toast/useErrorToast'
import useInfoToast from 'hooks/toast/useInfoToast'
import useSuccessToast from 'hooks/toast/useSuccessToast'
import useAdjustmentValidationErrorHandler from 'hooks/useAdjustmentValidationErrorHandler'
import { usePrintProvider } from 'providers/PrintProvider'
import type { Task } from 'providers/TaskProvider'
import { useTPCacheContext } from 'providers/TPCacheProvider'
import type { UseAutoPrintShippingLabelParams } from 'types'
import {
  allTasksComplete,
  isLabelPrinted,
  isShipmentGenerated,
  getSerialNumbersByOtcProduct,
  getOverriddenItems,
  getScannedTasksForShippingLabel,
} from 'utils'

// Persist print state outside component so it does not depend on re-mount when re-fetching order
const printState = { orderId: null as string | null, printRequestSent: false }

/**
 * handles auto printing of shipping labels after all packing scans tasks are complete.
 * triggers print when all conditions are met:
 * - all tasks complete
 * - order not in triage
 * - generates shipping label if not already generated
 */
export function useAutoPrintShippingLabel({
  order,
  tasks,
  packingTasksDataReady,
  shouldAutoPrintLabel,
  openPrintModal,
  closePrintModal,
  setTrackingNumber,
}: UseAutoPrintShippingLabelParams): void {
  const showSuccessToast = useSuccessToast(true)
  const showInfoToast = useInfoToast(true)
  const showErrorToast = useErrorToast(true)
  const handleAdjustmentValidationError = useAdjustmentValidationErrorHandler()

  const { getSavedPrinterId } = usePrintProvider()
  const { getPrinterById } = useTPCacheContext()
  const shippingLabelPrinterId = getSavedPrinterId(PrinterPurpose.ShippingLabel)
  const shippingLabelPrinter = shippingLabelPrinterId ? getPrinterById(shippingLabelPrinterId) : null

  // Reset state if orderId changed
  if (printState.orderId !== order._id) {
    printState.orderId = order._id
    printState.printRequestSent = false
  }

  const [generateShippingLabel] = useMutation(GENERATE_SHIPPING_LABEL, {
    onCompleted: () => showSuccessToast('Shipping label generated'),
    onError: error => {
      showErrorToast(`Failed to generate label: ${error?.message}`)
    },
  })

  const [printShippingLabel] = useMutation(PRINT_SHIPPING_LABEL, {
    refetchQueries: ['getFullOrder'],
    onCompleted: () => showSuccessToast('Print request sent.'),
    onError: error => {
      if (!handleAdjustmentValidationError(error)) showErrorToast(`Failed to print shipping label. ${error.message}`)
    },
  })

  // Returns: 'proceed' to print, 'retry' to allow retry after action, 'abort' to stop without retry
  const checkPrintability = useCallback((): 'proceed' | 'retry' | 'abort' => {
    if (order.inTriage) {
      showErrorToast('Unable to print shipping label while order is in Triage')
      return 'abort'
    }

    if (!shippingLabelPrinterId || !shippingLabelPrinter) {
      openPrintModal()
      return 'retry'
    }

    return 'proceed'
  }, [order.inTriage, shippingLabelPrinterId, shippingLabelPrinter, showErrorToast, openPrintModal])

  const executePrint = useCallback(
    async (currentTasks: Task[]) => {
      if (!shippingLabelPrinter) {
        showErrorToast('No shipping label printer selected')
        return
      }

      closePrintModal()

      // Generate shipping label if not already generated
      if (!isShipmentGenerated(order)) {
        showInfoToast('Requesting shipping label')

        const generateResult = await generateShippingLabel({
          variables: { coreOrderId: order.coreOrderId },
          refetchQueries: ['getFullOrder'],
        })

        if (!generateResult.data?.generateShippingLabel) {
          return
        }

        const trackingNumber = generateResult.data.generateShippingLabel.shipment?.tracking_number
        if (trackingNumber) {
          setTrackingNumber(trackingNumber)
        }
      }

      printShippingLabel({
        variables: {
          orderId: order._id,
          printerUrl: shippingLabelPrinter.GCPAddress,
          serialNumbersByOtcProduct: getSerialNumbersByOtcProduct(currentTasks, order.otcProducts || []),
          overrideItems: getOverriddenItems(currentTasks, order.otcProducts, order.packingMaterials),
          scannedTasks: getScannedTasksForShippingLabel(order.status, currentTasks),
        },
      })
    },
    [
      shippingLabelPrinter,
      closePrintModal,
      order,
      printShippingLabel,
      showErrorToast,
      showInfoToast,
      generateShippingLabel,
      setTrackingNumber,
    ],
  )

  // triggers auto print when all conditions are met
  useEffect(() => {
    if (!shouldAutoPrintLabel) return
    if (!packingTasksDataReady) return
    if (isLabelPrinted(order)) return
    if (printState.printRequestSent) return
    if (!allTasksComplete(tasks)) return

    const printability = checkPrintability()
    if (printability === 'retry') {
      return // retry after printer selection
    }
    printState.printRequestSent = true // Prevent duplicate calls and infinite error toasts
    if (printability === 'abort') {
      return
    }

    executePrint(tasks)
  }, [shouldAutoPrintLabel, packingTasksDataReady, tasks, order, checkPrintability, executePrint])
}

export default useAutoPrintShippingLabel
